import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Apple, Play, Mail, ArrowRight, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LAUNCH_DATE = new Date("2026-10-01T00:00:00Z");

const useCountdown = () => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, LAUNCH_DATE.getTime() - now);
  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff / 3600000) % 24),
    mins: Math.floor((diff / 60000) % 60),
    secs: Math.floor((diff / 1000) % 60),
  };
};

const Waitlist = ({ user }) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [joined, setJoined] = useState(false);
  const [count, setCount] = useState(null);
  const { days, hours, mins, secs } = useCountdown();
  const isJoined = joined || !!user;

  useEffect(() => {
    axios
      .get(`${API}/waitlist/count`)
      .then((r) => setCount(r.data.count))
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const r = await axios.post(`${API}/waitlist`, { email: email.trim() });
      setJoined(true);
      setCount(r.data.count);
      toast.success("You're on the list! Watch your inbox at launch.");
    } catch (err) {
      if (err.response?.status === 409) {
        toast.info("This email is already on the waitlist.");
        setJoined(true);
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const pad = (n) => String(n).padStart(2, "0");
  const units = [
    { label: "Days", value: pad(days), id: "countdown-days" },
    { label: "Hours", value: pad(hours), id: "countdown-hours" },
    { label: "Mins", value: pad(mins), id: "countdown-mins" },
    { label: "Secs", value: pad(secs), id: "countdown-secs" },
  ];

  return (
    <section id="waitlist" data-testid="waitlist-section" className="relative py-20 lg:py-32 border-t border-[#1E293B] overflow-hidden">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent" />
      <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[420px] w-[720px] rounded-full bg-cyan-500/8 blur-[130px]" />

      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <p className="mono-label">Early Access</p>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-4">
          Be first in line when InNewZ drops.
        </h2>
        <p className="text-slate-400 mt-4 leading-relaxed max-w-xl mx-auto">
          Waitlist members get lifetime premium perks, a founder badge, and first access to
          regional language packs.
        </p>

        <div data-testid="launch-countdown" className="mt-10 flex justify-center gap-3 sm:gap-4">
          {units.map((u) => (
            <div key={u.id} data-testid={u.id} className="w-16 sm:w-20 rounded-xl border border-[#1E293B] bg-slate-900/70 py-4">
              <div className="font-display text-2xl sm:text-3xl font-bold text-cyan-300">{u.value}</div>
              <div className="mt-1 text-[9px] font-mono-x uppercase tracking-widest text-slate-500">{u.label}</div>
            </div>
          ))}
        </div>

        {isJoined ? (
          <div data-testid="waitlist-success" className="mt-10 mx-auto max-w-md rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-6">
            <span className="mx-auto h-11 w-11 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center">
              <Check className="h-5 w-5 text-emerald-400" />
            </span>
            <p className="font-display font-semibold mt-4">
              You're in{user?.name ? `, ${user.name.split(" ")[0]}` : ""}.
            </p>
            <p className="text-sm text-slate-400 mt-1.5">
              We'll email you the moment early access opens.
              {count !== null && count > 0 && (
                <span className="block mt-1 text-cyan-300 font-mono-x text-xs uppercase tracking-widest">
                  {count.toLocaleString()} readers waiting
                </span>
              )}
            </p>
          </div>
        ) : (
          <form data-testid="waitlist-form" onSubmit={submit} className="mt-10 mx-auto max-w-md flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
              <input
                data-testid="waitlist-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full rounded-full border border-slate-700 bg-slate-900/80 py-3.5 pl-11 pr-4 text-sm text-slate-100 placeholder:text-slate-600 outline-none transition-colors duration-200 focus:border-cyan-500/60"
              />
            </div>
            <button
              data-testid="waitlist-submit-button"
              type="submit"
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 px-7 py-3.5 text-sm font-semibold text-white transition-[filter,transform,opacity] duration-200 hover:brightness-110 active:scale-95 disabled:opacity-60"
            >
              {loading ? "Joining…" : "Notify Me"}
              <ArrowRight className="h-4 w-4" />
            </button>
          </form>
        )}

        <div className="mt-12 flex flex-wrap justify-center gap-4">
          <button
            data-testid="app-store-button"
            className="inline-flex items-center gap-3 rounded-xl border border-slate-700 bg-slate-900/70 px-5 py-3 text-left transition-colors duration-200 hover:border-slate-500"
          >
            <Apple className="h-7 w-7 text-slate-200" />
            <span>
              <span className="block text-[9px] font-mono-x uppercase tracking-widest text-slate-500">Coming soon on</span>
              <span className="block text-sm font-semibold text-slate-100">App Store</span>
            </span>
          </button>
          <button
            data-testid="play-store-button"
            className="inline-flex items-center gap-3 rounded-xl border border-slate-700 bg-slate-900/70 px-5 py-3 text-left transition-colors duration-200 hover:border-slate-500"
          >
            <Play className="h-6 w-6 text-slate-200" />
            <span>
              <span className="block text-[9px] font-mono-x uppercase tracking-widest text-slate-500">Coming soon on</span>
              <span className="block text-sm font-semibold text-slate-100">Google Play</span>
            </span>
          </button>
        </div>
      </div>
    </section>
  );
};

export default Waitlist;
